/**
 * Frame capture — deterministic, fixed-resolution rendering of arbitrary camera
 * poses, for hosts that encode their own video (e.g. the Discover flythrough
 * exporter). Mirrors the editor's VideoRenderSession frame loop
 * (src/utils/videoRender/VideoRenderSession.ts):
 *
 * - The backbuffer is switched to the exact output size (RESOLUTION_FIXED,
 *   maxPixelRatio 1) for the whole session and restored on end().
 * - Frames are pumped off a visibility-independent MessageChannel yield and a
 *   manual `app.render()` (with `framerender` to drive LOD streaming), so a
 *   backgrounded tab keeps capturing.
 * - Each frame is held until splat sorting is current for the new pose: legacy
 *   per-instance sorters are kicked and awaited, and unified splats render
 *   until the engine's `frame:ready` reports ready with nothing loading.
 * - renderFrame() resolves in the same task as the final render, so the caller
 *   can read the canvas (new VideoFrame(canvas), drawImage) before the drawing
 *   buffer is cleared.
 * - Before a frame renders, `prepareFrame` (createViewer's capture swap driver)
 *   brings splat swaps up to date for its pose and tour progress, advancing swap
 *   transitions by the video's frame time (1 / fps) and waiting for swap loads.
 */
import * as pc from 'playcanvas';
import type { CameraPose, FrameCaptureFrameOptions, FrameCaptureSession } from '../types';
import { captureFrameDt } from './captureSwaps';

export const MIN_CAPTURE_DIMENSION = 16;
export const MAX_CAPTURE_DIMENSION = 8192;

export function validateCaptureSize(width: number, height: number): void {
    for (const [label, value] of [['width', width], ['height', height]] as const) {
        if (!Number.isInteger(value) || value < MIN_CAPTURE_DIMENSION || value > MAX_CAPTURE_DIMENSION) {
            throw new Error(`[StorySplat Viewer] Frame capture ${label} must be an integer from ${MIN_CAPTURE_DIMENSION} to ${MAX_CAPTURE_DIMENSION}.`);
        }
        if (value % 2 !== 0) {
            throw new Error(`[StorySplat Viewer] Frame capture ${label} must be even (video encoders require it).`);
        }
    }
}

export const MAX_CAPTURE_FPS = 1000;

/** `fps` is optional; when given it must be a positive frame rate. */
export function validateCaptureFps(fps: number | undefined): void {
    if (fps === undefined) return;
    if (typeof fps !== 'number' || !Number.isFinite(fps) || fps <= 0 || fps > MAX_CAPTURE_FPS) {
        throw new Error(`[StorySplat Viewer] Frame capture fps must be a number above 0 and at most ${MAX_CAPTURE_FPS}.`);
    }
}

/**
 * Visibility-independent yield. Browsers pause requestAnimationFrame and throttle
 * timers in background tabs; MessageChannel messages are still delivered.
 */
let yieldChannel: MessageChannel | null = null;
function backgroundYield(): Promise<void> {
    if (typeof MessageChannel === 'undefined') return Promise.resolve();
    if (!yieldChannel) yieldChannel = new MessageChannel();
    const channel = yieldChannel;
    return new Promise((resolve) => {
        channel.port1.onmessage = () => resolve();
        channel.port2.postMessage(0);
    });
}

/** One manual frame: re-pin the pose, drive LOD streaming, render (postrender fires synchronously). */
function renderTick(app: pc.Application, applyPose: () => void): Promise<void> {
    return backgroundYield().then(() => {
        applyPose();
        app.fire('framerender');
        app.render();
    });
}

/** Safety cap on ready-waiting so heavy streaming can't hang a capture forever. */
const MAX_TICKS_PER_FRAME = 90;
const LEGACY_SORT_TIMEOUT_MS = 500;

function sortLegacySplatsAndWait(app: pc.Application, cameraEntity: pc.Entity): Promise<void> {
    const components = app.root
        .findComponents('gsplat')
        .filter((c): c is pc.GSplatComponent => c instanceof pc.GSplatComponent && c.enabled && c.entity.enabled);
    return Promise.all(components.map((component) => new Promise<void>((resolve) => {
        const instance = component.instance; // null for unified components
        if (!instance || !instance.sorter) {
            resolve();
            return;
        }
        let settled = false;
        const finish = () => {
            if (!settled) {
                settled = true;
                resolve();
            }
        };
        instance.sorter.once('updated', finish);
        instance.sort(cameraEntity);
        setTimeout(finish, LEGACY_SORT_TIMEOUT_MS);
    }))).then(() => undefined);
}

/**
 * Render until the unified splats report ready for the camera (sorted, nothing loading).
 * Capped at MAX_TICKS_PER_FRAME ticks, or with `maxWaitMs` at that much time instead: a splat
 * swapped in (a unified one re-sorts from scratch) can outlast 90 quick ticks on a busy machine.
 */
export async function renderUntilSplatsReady(app: pc.Application, camComp: pc.CameraComponent, applyPose: () => void, maxWaitMs?: number): Promise<void> {
    let eventsThisTick = 0;
    let readyThisTick = true;
    const onFrameReady = (eventCamera: pc.CameraComponent, _layer: pc.Layer, ready: boolean, loadingCount: number) => {
        if (eventCamera !== camComp) return;
        eventsThisTick++;
        if (!ready || loadingCount > 0) readyThisTick = false;
    };
    const gsplatSystem = app.systems.gsplat;
    gsplatSystem?.on('frame:ready', onFrameReady);
    const deadline = maxWaitMs === undefined ? null : performance.now() + maxWaitMs;
    try {
        for (let tick = 0; deadline === null ? tick < MAX_TICKS_PER_FRAME : performance.now() < deadline; tick++) {
            eventsThisTick = 0;
            readyThisTick = true;
            await renderTick(app, applyPose);
            // No events = no unified splats rendering; one frame is enough.
            if (eventsThisTick === 0 || readyThisTick) return;
        }
    } finally {
        gsplatSystem?.off('frame:ready', onFrameReady);
    }
}

export interface FrameCaptureDeps {
    app: pc.Application;
    camera: pc.Entity;
    width: number;
    height: number;
    /** Frame rate of the video being captured; drives the time between frames (default 30). */
    fps?: number;
    /**
     * Awaited before each frame renders, with the camera already on `pose`: apply what the
     * frame should show (splat swaps) and wait until it is ready.
     */
    prepareFrame?: (pose: CameraPose, frame: { progress?: number; dtSec: number }) => Promise<void>;
    /** Called once by end(), after the canvas and camera are restored. */
    onEnd: () => void;
}

export function createFrameCaptureSession(deps: FrameCaptureDeps): FrameCaptureSession {
    const { app, camera, width, height, onEnd, prepareFrame } = deps;
    const frameDt = captureFrameDt(deps.fps);
    const camComp = camera.camera;
    if (!camComp) throw new Error('[StorySplat Viewer] Scene camera is not ready.');
    const device = app.graphicsDevice;
    const canvas = device.canvas as HTMLCanvasElement;

    const saved = {
        maxPixelRatio: device.maxPixelRatio,
        resolutionMode: app.resolutionMode,
        position: camera.getPosition().clone(),
        rotation: camera.getRotation().clone(),
        fov: camComp.fov,
    };

    const posePosition = new pc.Vec3();
    const poseRotation = new pc.Quat();
    let poseFov = saved.fov;
    let poseActive = false;
    const applyPose = () => {
        if (!poseActive) return;
        camera.setPosition(posePosition);
        camera.setRotation(poseRotation);
        camComp.fov = poseFov;
    };

    device.maxPixelRatio = 1;
    app.setCanvasResolution(pc.RESOLUTION_FIXED, width, height);

    let ended = false;
    let rendering = false;

    return {
        canvas,
        width,
        height,
        async renderFrame(pose: CameraPose, frame?: FrameCaptureFrameOptions): Promise<void> {
            if (ended) throw new Error('[StorySplat Viewer] Frame capture has ended.');
            if (rendering) throw new Error('[StorySplat Viewer] renderFrame is already running; await the previous frame.');
            rendering = true;
            try {
                posePosition.set(pose.position.x, pose.position.y, pose.position.z);
                poseRotation.set(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w).normalize();
                poseFov = pose.fov;
                poseActive = true;
                applyPose();
                if (prepareFrame) {
                    await prepareFrame(pose, { progress: frame?.progress, dtSec: frameDt });
                    if (ended) throw new Error('[StorySplat Viewer] Frame capture has ended.');
                    applyPose();
                }
                await sortLegacySplatsAndWait(app, camera);
                if (ended) throw new Error('[StorySplat Viewer] Frame capture has ended.');
                await renderUntilSplatsReady(app, camComp, applyPose);
            } finally {
                rendering = false;
            }
        },
        end(): void {
            if (ended) return;
            ended = true;
            poseActive = false;
            device.maxPixelRatio = saved.maxPixelRatio;
            camComp.fov = saved.fov;
            camera.setPosition(saved.position);
            camera.setRotation(saved.rotation);
            app.setCanvasResolution(saved.resolutionMode, canvas.clientWidth, canvas.clientHeight);
            app.renderNextFrame = true;
            onEnd();
        },
    };
}
